import React from 'react';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { ArrowRight, Play, Shield, Sparkles } from 'lucide-react';
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '../../lib/tenant';
import { MasterDashboardMockup } from './AppScreenMockups';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 * i, duration: 0.65, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

const TRUST = ['Branch ledger books', 'HP & hand loans', 'Day reports', 'WhatsApp reminders', 'Razorpay billing'];

export default function Hero() {
  return (
    <section id="home" className="ef-hero relative min-h-[100dvh] overflow-hidden pt-[4.5rem]">

      <div className="relative container mx-auto flex min-h-[calc(100dvh-4.5rem)] flex-col items-center justify-center gap-14 px-4 py-14 lg:flex-row lg:gap-12 lg:py-20">
        <div className="max-w-xl flex-1 text-center lg:max-w-[36rem] lg:text-left">
          <motion.div
            custom={0}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 backdrop-blur-md"
          >
            <Sparkles className="h-4 w-4 text-emerald-400" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">Launch edition · Now live</span>
          </motion.div>

          <motion.p
            custom={1}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-slate-500"
          >
            {PRODUCT_NAME}
          </motion.p>

          <motion.h1
            custom={2}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="font-display text-[2.65rem] font-semibold leading-[1.02] tracking-tight text-white sm:text-[3.5rem] lg:text-[3.9rem]"
          >
            The finance OS for{' '}
            <span className="relative">
              <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-200 bg-clip-text text-transparent">
                vehicle shops
              </span>
              <motion.span
                className="absolute -bottom-1 left-0 h-[3px] w-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </span>
            {' '}that scale.
          </motion.h1>

          <motion.p
            custom={3}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            {PRODUCT_TAGLINE} — branch ledgers, hire-purchase, hand loans, collections, WhatsApp reminders, and
            compliance-ready reports in one premium workspace.
          </motion.p>

          <motion.div
            custom={4}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start"
          >
            <NavLink
              to="/register?plan=pro"
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-3 text-sm font-semibold text-white shadow-xl shadow-emerald-900/50 transition-all hover:-translate-y-0.5 hover:shadow-emerald-900/60"
            >
              Start free trial
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </NavLink>
            <a
              href="#features"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white backdrop-blur-md transition-colors hover:bg-white/10"
            >
              <Play className="h-4 w-4 text-emerald-400" />
              Tour the product
            </a>
          </motion.div>

          <motion.div
            custom={5}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="mt-10 flex flex-wrap justify-center gap-2 lg:justify-start"
          >
            {TRUST.map((t) => (
              <span
                key={t}
                className="rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-slate-400 backdrop-blur-sm"
              >
                {t}
              </span>
            ))}
          </motion.div>

          <motion.div
            custom={6}
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-500 lg:justify-start"
          >
            <Shield className="h-4 w-4 text-emerald-500" />
            Tenant-isolated · Mobile-first · Razorpay billing
          </motion.div>
        </div>

        <motion.div
          custom={7}
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="w-full max-w-xl flex-1 lg:max-w-2xl"
        >
          <div className="ef-hero-product-stage">
            <MasterDashboardMockup />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
